import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ArmyBuilderConfig } from '../global/config';
import { HttpClientWithInFlightCache } from '../global/httpClient';
import { ListValidationSummary } from './list-validation-summary';
import { AppEvent, EventRegistration, EventRound, EventStanding, ForceEventSubmission, ForceReviewTarget } from './models';
import { SubmittableRegistration } from './submittable-registrations';

export interface EventListFilter {
    title?: string;
    startDateFrom?: string;
    startDateTo?: string;
    countryCode?: string;
    visibility?: string;
    lat?: number;
    lng?: number;
    radiusKm?: number;
    /** Past events (judged by end date, falling back to start date) are excluded by
     * default on the server — set true to include them too. */
    includePast?: boolean;
}

@Injectable({ providedIn: 'root' })
export class EventService {
    private config = inject(ArmyBuilderConfig);
    private http = inject(HttpClientWithInFlightCache);

    private get baseUrl() {
        return `${this.config.apiBaseUrl}/events`;
    }

    listEvents(gameId: string, filters?: EventListFilter): Observable<AppEvent[]> {
        const params = new URLSearchParams({ gameId });
        if (filters?.title) params.set('title', filters.title);
        if (filters?.startDateFrom) params.set('startDateFrom', filters.startDateFrom);
        if (filters?.startDateTo) params.set('startDateTo', filters.startDateTo);
        if (filters?.countryCode) params.set('countryCode', filters.countryCode);
        if (filters?.visibility) params.set('visibility', filters.visibility);
        if (filters?.lat != null && filters?.lng != null && filters?.radiusKm != null) {
            params.set('lat', String(filters.lat));
            params.set('lng', String(filters.lng));
            params.set('radiusKm', String(filters.radiusKm));
        }
        if (filters?.includePast) params.set('includePast', 'true');
        return this.http.get(`${this.baseUrl}?${params.toString()}`, { requiresLogin: false });
    }

    /** Powers the top-level, game-filterable browse page — a single query across several games. */
    listEventsAcrossGames(gameIds: string[], includePast = false): Observable<AppEvent[]> {
        const params = new URLSearchParams({ gameIds: gameIds.join(',') });
        if (includePast) params.set('includePast', 'true');
        return this.http.get(`${this.baseUrl}?${params.toString()}`, { requiresLogin: false });
    }

    listMyEvents(gameId: string): Observable<AppEvent[]> {
        const params = new URLSearchParams({ gameId });
        return this.http.get(`${this.baseUrl}/mine?${params.toString()}`, { requiresLogin: true });
    }

    listMyRegistrations(gameId?: string): Observable<SubmittableRegistration[]> {
        const params = new URLSearchParams();
        if (gameId) params.set('gameId', gameId);
        const query = params.toString();
        return this.http.get(`${this.baseUrl}/registrations/mine${query ? '?' + query : ''}`, { requiresLogin: true });
    }

    listPendingApprovals(gameId?: string): Observable<AppEvent[]> {
        const params = new URLSearchParams();
        if (gameId) params.set('gameId', gameId);
        const query = params.toString();
        return this.http.get(`${this.baseUrl}/pending-approvals${query ? '?' + query : ''}`, { requiresLogin: true });
    }

    approveEvent(id: string, reviewNotes?: string): Observable<AppEvent> {
        return this.http.put(`${this.baseUrl}/${id}/approve`, { reviewNotes }, { requiresLogin: true });
    }

    rejectEvent(id: string, reviewNotes: string): Observable<AppEvent> {
        return this.http.put(`${this.baseUrl}/${id}/reject`, { reviewNotes }, { requiresLogin: true });
    }

    getEvent(id: string): Observable<AppEvent> {
        return this.http.get(`${this.baseUrl}/${id}`, { requiresLogin: false });
    }

    createEvent(event: Partial<AppEvent>): Observable<AppEvent> {
        return this.http.post(`${this.baseUrl}`, event, { requiresLogin: true });
    }

    updateEvent(id: string, event: Partial<AppEvent>): Observable<AppEvent> {
        return this.http.put(`${this.baseUrl}/${id}`, event, { requiresLogin: true });
    }

    deleteEvent(id: string): Observable<void> {
        return this.http.delete(`${this.baseUrl}/${id}`, undefined, { requiresLogin: true });
    }

    cancelEvent(id: string): Observable<AppEvent> {
        return this.http.put(`${this.baseUrl}/${id}/cancel`, {}, { requiresLogin: true });
    }

    submitForReview(id: string): Observable<AppEvent> {
        return this.http.put(`${this.baseUrl}/${id}/submit-for-review`, {}, { requiresLogin: true });
    }

    withdrawEvent(id: string): Observable<AppEvent> {
        return this.http.put(`${this.baseUrl}/${id}/withdraw`, {}, { requiresLogin: true });
    }

    listRegistrations(eventId: string): Observable<EventRegistration[]> {
        return this.http.get(`${this.baseUrl}/${eventId}/registrations`, { requiresLogin: true });
    }

    register(eventId: string, details: { displayName: string; contactEmail: string }): Observable<EventRegistration> {
        return this.http.post(`${this.baseUrl}/${eventId}/registrations`, details, { requiresLogin: true });
    }

    getMyRegistration(eventId: string): Observable<EventRegistration | null> {
        return this.http.get(`${this.baseUrl}/${eventId}/registrations/mine`, { requiresLogin: true });
    }

    updateRegistrationDetails(
        eventId: string,
        registrationId: string,
        details: { displayName: string; contactEmail: string }
    ): Observable<EventRegistration> {
        return this.http.put(`${this.baseUrl}/${eventId}/registrations/${registrationId}/details`, details, {
            requiresLogin: true
        });
    }

    updateRegistrationStatus(
        eventId: string,
        registrationId: string,
        status: string,
        notes?: string
    ): Observable<EventRegistration> {
        return this.http.put(
            `${this.baseUrl}/${eventId}/registrations/${registrationId}/status`,
            { status, notes },
            { requiresLogin: true }
        );
    }

    setListValidation(
        eventId: string,
        registrationId: string,
        data: { slotLabel: string; slotId?: number; validationSummary: ListValidationSummary }
    ): Observable<EventRegistration> {
        return this.http.put(`${this.baseUrl}/${eventId}/registrations/${registrationId}/list-validation`, data, {
            requiresLogin: true
        });
    }

    submitList(
        eventId: string,
        registrationId: string,
        data: {
            slotLabel: string;
            slotId?: number;
            submissionKind: 'force' | 'text';
            forceId?: string;
            snapshotData?: any;
            textSubmission?: string;
            validationSummary?: ListValidationSummary;
        }
    ): Observable<EventRegistration> {
        return this.http.put(`${this.baseUrl}/${eventId}/registrations/${registrationId}/submit-list`, data, { requiresLogin: true });
    }

    withdrawList(eventId: string, registrationId: string, slotLabel: string, slotId?: number): Observable<EventRegistration> {
        return this.http.put(
            `${this.baseUrl}/${eventId}/registrations/${registrationId}/withdraw-list`,
            { slotLabel, slotId },
            { requiresLogin: true }
        );
    }

    /** Organiser signs a submitted list off. Optional -- an un-actioned list is still valid. */
    acceptListSubmission(
        eventId: string,
        registrationId: string,
        data: { slotLabel: string; slotId?: number; notes?: string }
    ): Observable<EventRegistration> {
        return this.http.put(`${this.baseUrl}/${eventId}/registrations/${registrationId}/accept-list`, data, { requiresLogin: true });
    }

    rejectListSubmission(
        eventId: string,
        registrationId: string,
        data: { slotLabel: string; slotId?: number; notes?: string }
    ): Observable<EventRegistration> {
        return this.http.put(
            `${this.baseUrl}/${eventId}/registrations/${registrationId}/reject-list`,
            data,
            { requiresLogin: true }
        );
    }

    getRounds(eventId: string): Observable<EventRound[]> {
        return this.http.get(`${this.baseUrl}/${eventId}/rounds`, { requiresLogin: true });
    }

    startRound(eventId: string, roundNumber: number): Observable<EventRound> {
        return this.http.post(`${this.baseUrl}/${eventId}/rounds`, { roundNumber }, { requiresLogin: true });
    }

    updateRound(eventId: string, roundId: string, data: Partial<EventRound>): Observable<EventRound> {
        return this.http.put(`${this.baseUrl}/${eventId}/rounds/${roundId}`, data, { requiresLogin: true });
    }

    updateRoundStatus(
        eventId: string,
        roundId: string,
        data: { status?: string; startedAt?: string; completedAt?: string }
    ): Observable<EventRound> {
        return this.http.put(`${this.baseUrl}/${eventId}/rounds/${roundId}`, data, { requiresLogin: true });
    }

    reportResult(
        eventId: string,
        roundId: string,
        pairingId: string,
        data: { outcome: 'WIN' | 'LOSS' | 'DRAW'; myValues: Record<string, number>; opponentValues: Record<string, number> }
    ): Observable<EventRound> {
        return this.http.put(`${this.baseUrl}/${eventId}/rounds/${roundId}/pairings/${pairingId}/result`, data, { requiresLogin: true });
    }

    confirmResult(eventId: string, roundId: string, pairingId: string): Observable<EventRound> {
        return this.http.put(
            `${this.baseUrl}/${eventId}/rounds/${roundId}/pairings/${pairingId}/result/confirm`,
            {},
            { requiresLogin: true }
        );
    }

    disputeResult(eventId: string, roundId: string, pairingId: string): Observable<EventRound> {
        return this.http.put(
            `${this.baseUrl}/${eventId}/rounds/${roundId}/pairings/${pairingId}/result/dispute`,
            {},
            { requiresLogin: true }
        );
    }

    overrideResult(
        eventId: string,
        roundId: string,
        pairingId: string,
        data: {
            outcome: 'A_WIN' | 'B_WIN' | 'DRAW';
            participantAValues: Record<string, number>;
            participantBValues: Record<string, number>;
        }
    ): Observable<EventRound> {
        return this.http.put(`${this.baseUrl}/${eventId}/rounds/${roundId}/pairings/${pairingId}/result/override`, data, {
            requiresLogin: true
        });
    }

    editPairing(
        eventId: string,
        roundId: string,
        pairingId: string,
        data: { participantAUserId?: string; participantBUserId?: string | null; isBye?: boolean; tableNumber?: number | null }
    ): Observable<EventRound> {
        return this.http.put(`${this.baseUrl}/${eventId}/rounds/${roundId}/pairings/${pairingId}`, data, { requiresLogin: true });
    }

    regenerateRound(eventId: string, roundId: string): Observable<EventRound> {
        return this.http.post(`${this.baseUrl}/${eventId}/rounds/${roundId}/regenerate`, {}, { requiresLogin: true });
    }

    /** Organiser-only: wipes a pairing's result so it reads as never reported. */
    clearResult(eventId: string, roundId: string, pairingId: string): Observable<EventRound> {
        return this.http.delete(`${this.baseUrl}/${eventId}/rounds/${roundId}/pairings/${pairingId}/result`, { requiresLogin: true });
    }

    /** Deletes a round outright, with no replacement drawn -- unlike regenerateRound. */
    discardRound(eventId: string, roundId: string): Observable<void> {
        return this.http.delete(`${this.baseUrl}/${eventId}/rounds/${roundId}`, { requiresLogin: true });
    }

    getStandings(eventId: string): Observable<EventStanding[]> {
        return this.http.get(`${this.baseUrl}/${eventId}/standings`, { requiresLogin: false });
    }

    /** Events this force sits in that the caller organises, so can accept or reject on. */
    getForceReviewTargets(forceId: string): Observable<ForceReviewTarget[]> {
        return this.http.get(`${this.baseUrl}/force-review-targets/${forceId}`, { requiresLogin: true });
    }

    getForceEventSubmission(forceId: string): Observable<ForceEventSubmission | null> {
        return this.http.get(`${this.baseUrl}/force-submission/${forceId}`, { requiresLogin: true });
    }

    checkIn(eventId: string, registrationId: string): Observable<EventRegistration> {
        return this.http.put(`${this.baseUrl}/${eventId}/registrations/${registrationId}/check-in`, {}, { requiresLogin: true });
    }

    markReadyForRound(eventId: string, registrationId: string, roundNumber: number): Observable<EventRegistration> {
        return this.http.put(`${this.baseUrl}/${eventId}/registrations/${registrationId}/ready`, { roundNumber }, { requiresLogin: true });
    }

    generateTestRegistrations(eventId: string, count: number, forceIds: string[]): Observable<{ created: number }> {
        return this.http.post(`${this.baseUrl}/${eventId}/registrations/generate-test`, { count, forceIds }, { requiresLogin: true });
    }

    removeTestRegistrations(eventId: string): Observable<{ deleted: number }> {
        return this.http.delete(`${this.baseUrl}/${eventId}/registrations/test-data`, undefined, { requiresLogin: true });
    }
}
